import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import ENV from "../../../config/env";
import "./Sidebar.css";

// Assets imports with corrected paths
import eduoneLogo from "../../../assets/images/SVG/eduoneLogo.svg";
import sidebarExpandIcon from "../../../assets/images/sidebarexpan icon.png";
import sidebarCollapseIcon from "../../../assets/images/sidebarclosepanicon.png";

// SVGs
import dashboardIcon from "../../../assets/images/SVG/Dashbordnewicon.svg";
import dashboardIconActive from "../../../assets/images/SVG/Dashbordnewiconactive.svg";
import examManagerIcon from "../../../assets/images/SVG/exam-manager.svg";
import examManagerIconActive from "../../../assets/images/SVG/exam-manager-active.svg";
import resultIcon from "../../../assets/images/SVG/result.svg";
import resultIconActive from "../../../assets/images/SVG/result-active.svg";
import settingsIcon from "../../../assets/images/SVG/Settings.svg";

const ROUTE_BY_ID = {
  dashboard: "/dashboard",
  examManager: "/exam-manager",
  yourResult: "/your-result",
  settingsIcon: "/settings",
};

const getActiveFromPath = (pathname, routeById = ROUTE_BY_ID) => {
  if (!pathname) return "dashboard";

  const entry = Object.entries(routeById).find(
    ([, r]) => pathname === r || (r !== "/" && pathname.startsWith(r))
  );
  return entry ? entry[0] : "dashboard";
};

const Sidebar = ({ onExpansionChange, isExpanded: propIsExpanded }) => {
  const navigate = useNavigate();
  const location = useLocation();

  const [activeItem, setActiveItem] = useState(() => getActiveFromPath(location.pathname, ROUTE_BY_ID));
  const [expandedMenus, setExpandedMenus] = useState({});
  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window === "undefined") return false;
    return window.innerWidth <= 768;
  });

  // Dynamically track window resize for responsive layout updates
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const isControlled = typeof propIsExpanded === "boolean";
  const [uncontrolledExpanded, setUncontrolledExpanded] = useState(() => {
    if (typeof window === "undefined") return true;
    return window.innerWidth > 768;
  });

  const isExpanded = isControlled ? propIsExpanded : uncontrolledExpanded;

  const expandedRef = useRef(isExpanded);
  useEffect(() => {
    expandedRef.current = isExpanded;
  }, [isExpanded]);

  const setExpanded = useCallback(
    (nextOrUpdater) => {
      const prev = expandedRef.current;
      const next = typeof nextOrUpdater === "function" ? nextOrUpdater(prev) : nextOrUpdater;
      if (!isControlled) setUncontrolledExpanded(next);
      onExpansionChange?.(next);
    },
    [isControlled, onExpansionChange]
  );

  // Sync active item with route path changes
  useEffect(() => {
    setActiveItem(getActiveFromPath(location.pathname, ROUTE_BY_ID));
  }, [location.pathname]);

  // Close when viewport becomes mobile
  useEffect(() => {
    if (isMobile) {
      setExpanded(false);
    }
  }, [isMobile, setExpanded]);

  // Body scroll lock on mobile when open
  useEffect(() => {
    if (!isMobile) return;
    if (isExpanded) document.body.classList.add("no-scroll");
    else document.body.classList.remove("no-scroll");
    return () => document.body.classList.remove("no-scroll");
  }, [isMobile, isExpanded]);

  // Listen for header-triggered events
  useEffect(() => {
    const toggle = () => isMobile && setExpanded((p) => !p);
    const open = () => isMobile && setExpanded(true);
    const close = () => isMobile && setExpanded(false);
    const setActive = (e) => setActiveItem(e.detail);

    window.addEventListener("sidebar:toggle", toggle);
    window.addEventListener("sidebar:open", open);
    window.addEventListener("sidebar:close", close);
    window.addEventListener("sidebar:setActive", setActive);
    return () => {
      window.removeEventListener("sidebar:toggle", toggle);
      window.removeEventListener("sidebar:open", open);
      window.removeEventListener("sidebar:close", close);
      window.removeEventListener("sidebar:setActive", setActive);
    };
  }, [isMobile, setExpanded]);

  const handleItemClick = (itemId, hasSubMenu = false) => {
    if (hasSubMenu) {
      if (!isExpanded) {
        setExpanded(true);
      }
      setExpandedMenus(prev => {
        const isOpening = !prev[itemId];
        const next = { [itemId]: isOpening };
        return next;
      });
      return;
    }

    setActiveItem(itemId);
    const path = ROUTE_BY_ID[itemId];
    if (path) navigate(path);
    if (isMobile) setExpanded(false);
  };

  const handleLogoClick = () => {
    navigate('/dashboard');
    setActiveItem('dashboard');
  };

  const menuItems = [
    {
      id: "dashboard",
      icon: dashboardIcon,
      activeIcon: dashboardIconActive,
      label: "Dashboard",
    },
    {
      id: "examManager",
      icon: examManagerIcon,
      activeIcon: examManagerIconActive,
      label: "Your Exams",
    },
    {
      id: "yourResult",
      icon: resultIcon,
      activeIcon: resultIconActive,
      label: "Your Result",
    },
  ];

  // Auto-expand menu when a child is active
  useEffect(() => {
    const parentItem = menuItems.find(
      item => item.hasSubMenu && item.subItems?.some(sub => sub.id === activeItem)
    );
    if (parentItem) {
      setExpandedMenus(prev => ({ ...prev, [parentItem.id]: true }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeItem]);

  const handleToggleExpand = () => {
    setExpanded((prev) => !prev);
  };

  return (
    <>
      {isMobile && (
        <div
          className={`sidebar-backdrop ${isExpanded ? "show" : ""}`}
          onClick={() => setExpanded(false)}
          aria-hidden={!isExpanded}
        />
      )}

      <div
        className={`sidebar ${isExpanded ? "expanded" : "collapsed"} ${isMobile ? "is-mobile" : ""}`}
        role="navigation"
        aria-label="Primary"
      >
        {/* Close (mobile) */}
        {isMobile && (
          <button
            type="button"
            className="sidebar-close"
            aria-label="Close menu"
            onClick={() => setExpanded(false)}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
              <path d="M6 6l12 12M18 6L6 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
            </svg>
          </button>
        )}

        <div className="sidebar-header">
          {isExpanded && (
            <img
              src={eduoneLogo}
              alt="EduOne"
              className="sidebar-logo"
              onClick={handleLogoClick}
              style={{ cursor: 'pointer' }}
            />
          )}
          <button
            type="button"
            className="sidebar-expand-btn"
            aria-label={isExpanded ? "Collapse sidebar" : "Expand sidebar"}
            onClick={handleToggleExpand}
          >
            <img
              className="sidebar-expand-icon"
              src={isExpanded ? sidebarCollapseIcon : sidebarExpandIcon}
              alt=""
              aria-hidden="true"
            />
          </button>
        </div>

        <div className="sidebar-content">
          <nav className="sidebar-nav">
            {menuItems.map((item) => {
              const hasActiveChild = item.hasSubMenu && item.subItems?.some(sub => sub.id === activeItem);
              return (
                <div key={item.id}>
                  <button
                    className={`nav-item 
                      ${item.id === activeItem && !item.hasSubMenu ? "active" : ""} 
                      ${hasActiveChild ? "has-active-child" : ""}
                      ${item.hasSubMenu ? "has-submenu" : ""}
                    `}
                    onClick={() => handleItemClick(item.id, item.hasSubMenu)}
                    title={!isExpanded ? item.label : ""}
                  >
                    <span className="nav-icon">
                      <img
                        src={item.id === activeItem && item.activeIcon ? item.activeIcon : item.icon}
                        alt={item.label}
                        className="nav-icon-svg"
                        style={{
                          ...(item.id === 'dashboard' ? { width: 18, height: 18 } : {}),
                        }}
                      />
                    </span>
                    <span className="nav-label">{item.label}</span>
                    {item.hasSubMenu && (
                      <span className={`submenu-arrow ${expandedMenus[item.id] ? "expanded" : ""}`}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                          <path d="M6 9l6 6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      </span>
                    )}
                  </button>

                  {item.hasSubMenu && expandedMenus[item.id] && (
                    <div className="submenu">
                      {item.subItems.map((subItem) => (
                        <button
                          key={subItem.id}
                          className={`nav-item submenu-item ${subItem.id === activeItem ? "active" : ""}`}
                          onClick={() => handleItemClick(subItem.id)}
                          title={!isExpanded ? subItem.label : ""}
                        >
                          <span className="nav-icon">
                            <img
                              src={subItem.id === activeItem && subItem.activeIcon ? subItem.activeIcon : subItem.icon}
                              alt={subItem.label}
                              className="nav-icon-svg"
                            />
                          </span>
                          <span className="nav-label">{subItem.label}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </nav>
        </div>

        <div className="sidebar-footer">
          <button
            className={`nav-item ${activeItem === "settingsIcon" ? "active" : ""}`}
            title={!isExpanded ? "Settings" : ""}
            onClick={() => handleItemClick("settingsIcon")}
          >
            <span className="nav-icon">
              <img
                src={settingsIcon}
                alt="Settings"
                className="nav-icon-svg"
              />
            </span>
            <span className="nav-label">Settings</span>
          </button>
          {isExpanded && ENV.VERSION && (
            <div className="sidebar-version">
              {ENV.VERSION}
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default Sidebar;
